// Builds the personalised goal report PDF in the browser (no server).
// jsPDF's built-in Helvetica has no ₹ glyph, so amounts are written "Rs."
import { jsPDF } from "jspdf";
import logoUrl from "@/assets/logo-horizontal.png";
import { glideAllocation } from "@/lib/finance";

const NAVY = [17, 44, 80];
const ORANGE = [241, 119, 30];
const GOLD = [228, 181, 60];
const SLATE = [91, 102, 114];

const rs = (s) => String(s).replace(/₹/g, "Rs. ");

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

export async function buildGoalReport({ goal, values, result, name }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  let y = 0;

  // Header
  const logo = await loadImage(logoUrl);
  if (logo) doc.addImage(logo, "PNG", M, 28, 120, 46);
  doc.setFillColor(...GOLD);
  doc.rect(0, 90, W, 2, "F");
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...SLATE);
  doc.text(`Prepared ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`, W - M, 56, { align: "right" });

  y = 132;
  doc.setFontSize(10).setTextColor(...ORANGE).text(`${goal.name.toUpperCase()} PLAN`, M, y);
  y += 28;
  doc.setFont("times", "normal").setFontSize(26).setTextColor(...NAVY);
  doc.text(`${name ? name.split(" ")[0] + "'s" : "Your"} ${rs(goal.name)} Plan`, M, y);
  y += 18;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(...SLATE);
  doc.text("An illustrative estimate based on the assumptions you chose.", M, y);

  // Headline box
  y += 26;
  doc.setFillColor(...NAVY);
  doc.rect(M, y, W - 2 * M, 78, "F");
  doc.setFontSize(9).setTextColor(...GOLD).text(rs(result.headline.label).toUpperCase(), M + 20, y + 26);
  doc.setFont("courier", "bold").setFontSize(24).setTextColor(255, 255, 255).text(rs(result.headline.value), M + 20, y + 58);
  y += 104;

  // Stats grid
  const colW = (W - 2 * M) / 2;
  result.stats.forEach((s, i) => {
    const x = M + (i % 2) * colW;
    const yy = y + Math.floor(i / 2) * 52;
    doc.setDrawColor(...GOLD).setLineWidth(0.8).line(x, yy, x + colW - 16, yy);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...SLATE).text(rs(s.label).toUpperCase(), x, yy + 16);
    doc.setFont("courier", "bold").setFontSize(14).setTextColor(...NAVY).text(rs(s.value), x, yy + 36);
  });
  y += Math.ceil(result.stats.length / 2) * 52 + 16;

  // Inputs
  doc.setFont("times", "normal").setFontSize(15).setTextColor(...NAVY).text("Your assumptions", M, y);
  y += 16;
  goal.inputs.forEach((inp, i) => {
    const x = M + (i % 2) * colW;
    const yy = y + Math.floor(i / 2) * 18;
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...SLATE).text(rs(inp.label), x, yy);
    doc.setFont("courier", "normal").setTextColor(...NAVY).text(rs(inp.format ? inp.format(values[inp.key]) : values[inp.key]), x + colW - 24, yy, { align: "right" });
  });
  y += Math.ceil(goal.inputs.length / 2) * 18 + 18;

  // Allocation
  const a = result.allocation || glideAllocation(result.yearsLeft);
  doc.setFont("times", "normal").setFontSize(15).setTextColor(...NAVY).text("Suggested starting mix (illustrative)", M, y);
  y += 14;
  const barW = W - 2 * M;
  const parts = [
    [a.equity, NAVY, "Equity"],
    [a.debt, [126, 140, 155], "Debt & Bonds"],
    [a.gold, GOLD, "Gold"],
  ];
  let bx = M;
  parts.forEach(([p, c, label]) => {
    if (!p) return;
    const w = (barW * p) / 100;
    doc.setFillColor(...c).rect(bx, y, w - 2, 16, "F");
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...SLATE).text(`${label} ${p}%`, bx, y + 30);
    bx += w;
  });
  y += 50;

  // Year table (sampled to fit)
  const rows = (result.series || []).filter((_, i, arr) => arr.length <= 12 || i % Math.ceil(arr.length / 12) === 0 || i === arr.length - 1);
  if (rows.length && y < H - 220) {
    doc.setFont("times", "normal").setFontSize(15).setTextColor(...NAVY).text("Year by year", M, y);
    y += 16;
    doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...SLATE);
    const cl = result.chartLabels || { value: "Estimated value", invested: "Invested" };
    doc.text("YEAR", M, y).text(cl.invested.toUpperCase(), M + 120, y).text(cl.value.toUpperCase(), M + 260, y);
    y += 12;
    doc.setFont("courier", "normal").setFontSize(9).setTextColor(...NAVY);
    const fmt = (n) => "Rs. " + Math.round(n).toLocaleString("en-IN");
    rows.forEach((r) => {
      if (y > H - 110) return;
      doc.text(String(r.year), M, y).text(fmt(r.invested), M + 120, y).text(fmt(r.value), M + 260, y);
      y += 13;
    });
  }

  // Next steps + disclaimer
  doc.setFillColor(247, 244, 238).rect(0, H - 96, W, 96, "F");
  doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(...NAVY);
  doc.text("Next step: book a free 20-minute goal call  ·  +91 75067 84004  ·  info@raoinvestmentsolution.com", M, H - 74);
  doc.setFont("helvetica", "normal").setFontSize(7).setTextColor(...SLATE);
  doc.text(
    doc.splitTextToSize(
      "Illustrative estimate only, based on the assumptions shown. Not a guarantee of returns and not investment advice. Mutual fund investments are subject to market risks; read all scheme related documents carefully. Rao Investment Solutions is an AMFI-registered Mutual Fund Distributor (ARN-261755), a unit of Rao Enterprises.",
      W - 2 * M
    ),
    M,
    H - 56
  );

  return doc;
}

export async function downloadGoalReport(args) {
  const doc = await buildGoalReport(args);
  doc.save(`RIS-${args.goal.slug}-plan.pdf`);
}
